import React, { useState, useRef, useEffect } from 'react';
import useElementHeight from '../../../hooks/useElementHeight.js';
import { ActionButtonWrapper, BackButton, EvHeader, StatusView, StatusScreen } from '../../../components';
import { useSession } from '../../../context/SessionContext.jsx';
import { useToast } from '../../../context/ToastContext.jsx';
import { usePayments, AUTH_UNCONFIRMED_MESSAGE } from '../../../hooks/usePayments.js';
import { useCancelAttempt } from '../../../hooks/useCancelAttempt.jsx';
import ThreeDSFrame from '../../Payments/ThreeDSFrame/ThreeDSFrame.jsx';
import { patchReceipt } from '../../../api/sessionsApi.js';
import { storeReceiptInfo } from '../../../services/receipts.service.js';
import { PAYMENT_STEPS } from '../../../constants/steps.js';
import { STATUS_KEYS as STATUS } from '../../../constants/statusKeys.js';
import CardAuthorizationStep from './CardAuthorizationStep.jsx';
import ReceiptStep from './ReceiptStep.jsx';
import { computeCreditBreakdown } from '../AmountPicker/computeCreditBreakdown.js';
import './MakePayment.less';

const MakePayment = ({ handleIncreaseStep, handleDecreaseStep }) => {
    const [receiptInfo, setReceiptInfo] = useState(null);
    const [authData, setAuthData] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    // Checking the server for an authorization this page did not start
    // (after a reload or app resume) — see the effect below.
    const [isReconciling, setIsReconciling] = useState(false);
    
    const { ref, height } = useElementHeight();
    const cardStepRef = useRef(null);
    const receiptStepRef = useRef(null);
    
    // Get session state and payment functions
    const { state: { amountCents, currency, stationMeta, paymentStep, stationId, publicId, sessionUid, payment, appliedCredit }, setPaymentStep, setStep, update } = useSession();
    const { confirmAndCancel } = useCancelAttempt();
    const { preauth, reconcileAuthorization, isAuthorizing, threeds } = usePayments();
    const { showToast } = useToast();

    // Payment substep: 'details' | 'receipt' | 'processing'
    const currentStep = paymentStep || PAYMENT_STEPS.DETAILS;

    // Credit-adjusted authorization amount. The customer's selected
    // budget (amountCents) is the TOTAL charging budget; the card
    // only needs to be authorized for the portion NOT covered by the
    // applied redeem code. Server is the final authority — but the
    // FE must show and send the correct number, otherwise Fiserv
    // gets asked to hold the pre-credit amount.
    const codeAmountCents = appliedCredit
        ? Math.round(Number(appliedCredit.amount || 0) * 100)
        : 0;
    const { cardAuthCents, fullyCovered } = computeCreditBreakdown({
        selectedBudgetCents: amountCents,
        codeAmountCents,
    });

    // Displayed on the card authorization form.
    const formattedAmount = (cardAuthCents > 0)
        ? `${(cardAuthCents / 100).toFixed(2)} ${currency}`
        : '';

    // Fully-covered short-circuit. When the credit covers the whole
    // selected budget there's no card auth to do, so we skip the
    // DETAILS substep entirely and land the customer on RECEIPT so
    // they can enter their email and start charging. Guarded so the
    // effect only fires once when they land here — otherwise the
    // Back button from RECEIPT would immediately bounce them forward.
    useEffect(() => {
        if (fullyCovered && currentStep === PAYMENT_STEPS.DETAILS) {
            console.log('[MakePayment] Fully-covered credit — skipping card details');
            setPaymentStep(PAYMENT_STEPS.RECEIPT);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fullyCovered]);

    // A reload or app resume during an authorization leaves no attempt on
    // this page (SessionContext drops the stale 'authorizing' and sets
    // needsReconcile). Ask the server before the driver can press Authorize
    // again: an approval that already happened is adopted, never repeated.
    const needsReconcile = Boolean(payment?.needsReconcile);
    useEffect(() => {
        if (!needsReconcile || !sessionUid) return undefined;
        let live = true;
        setIsReconciling(true);
        (async () => {
            const outcome = await reconcileAuthorization();
            if (!live) return;
            setIsReconciling(false);
            if (outcome === 'authorized') {
                update({ authorizedAmount: cardAuthCents, sessionBudgetCents: amountCents });
                showToast({ message: 'Payment authorized.', type: 'success' });
                setPaymentStep(PAYMENT_STEPS.RECEIPT);
            } else if (outcome === 'pending' || outcome === 'unknown') {
                showToast({ message: AUTH_UNCONFIRMED_MESSAGE, type: 'warning' });
            }
        })();
        return () => { live = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsReconcile, sessionUid]);

    // Handle card authorization
    const handleAuthorize = async ({ card, billing }) => {
        console.log('[MakePayment]  Starting payment authorization');
        
        // Set to PROCESSING substep
        setPaymentStep(PAYMENT_STEPS.PROCESSING);
        
        try {
            // Call preauth with card details
            const result = await preauth({
                card,
                billing
            });

            if (result.cancelled) {
                // The driver cancelled. If the cancel goes through it resets
                // the wizard; if it is refused, the driver must not be left
                // on the button-less processing screen.
                setPaymentStep(PAYMENT_STEPS.DETAILS);
                return;
            }

            if (result.success) {
                console.log('[MakePayment]  Payment authorized successfully');
                
                // Extract last 4 digits from card number for display
                const last4 = card.number.replace(/\s/g, '').slice(-4);
                
                // Extract authorization ID from payment result
                const authorizationId = result.data?.payment?.transactionId || result.data?.transactionId;
                
                console.log('[MakePayment] Payment details:', {
                    authorizationId,
                    cardLast4: last4,
                    correlationId: result.data?.correlationId,
                    isDemo: result.isDemo
                });
                
                // Store auth data for receipt step
                setAuthData({
                    cardLast4: last4,
                    authorizationId,
                    ...result.data
                });
                
                // Update session state with payment details including authorizationId.
                // CRITICAL: authorizedAmount now reflects the CARD-side amount only
                // (budget - credit). sessionBudgetCents stays at the total budget
                // because downstream ETA / budget-cap logic bills against the total
                // charging budget, not the card hold.
                update({
                    payment: {
                        ...payment,
                        authorizationId,
                        cardLast4: last4,
                        status: 'authorized'
                    },
                    authorizedAmount: cardAuthCents, // What Fiserv actually held on the card
                    sessionBudgetCents: amountCents, // Total charging budget (card auth + credit)
                });
                
                // Show success toast
                showToast({
                    message: 'Payment authorized.',
                    type: 'success'
                });
                
                // Brief delay to show success message
                await new Promise(resolve => setTimeout(resolve, 1000));
                
                console.log('[MakePayment] 📝 Moving to receipt step');
                // Move to receipt step
                setPaymentStep(PAYMENT_STEPS.RECEIPT);
            } else {
                console.error('[MakePayment]  Payment authorization failed:', {
                    error: result.error,
                    correlationId: result.correlationId
                });
                
                // Go back to DETAILS step on error
                setPaymentStep(PAYMENT_STEPS.DETAILS);
                
                // Show error toast
                showToast({
                    message: result.error || 'Payment authorization failed. Please try again.',
                    type: 'error'
                });
            }
        } catch (e) {
            console.error('[MakePayment]  Payment exception:', {
                error: e.message,
                stack: e.stack
            });
            
            // Go back to DETAILS step on exception
            setPaymentStep(PAYMENT_STEPS.DETAILS);
            
            // Show error toast
            showToast({
                // Never the exception text (C-11); it is logged above.
                message: 'We could not authorize your card right now. Please try again or use another card.',
                type: 'error'
            });
        }
    };

    // Handle receipt submission and start charging
    const handleStartCharging = async (receipt) => {
        console.log('[MakePayment] Starting charging flow');
        
        // Validate that we have session_uid
        if (!sessionUid) {
            console.error('[MakePayment] No session UID found');
            showToast({
                message: 'Session not initialized. Please restart the charging flow.',
                type: 'error'
            });
            return;
        }

        // Validate that payment is authorized — unless the applied
        // credit fully covers the selected budget, in which case
        // there IS no card authorization to check for. The server
        // (payment/bootstrap) will set billing_mode='CREDIT' and
        // consume the redeem code atomically at that moment.
        if (!fullyCovered && !payment?.authorizationId && !authData?.authorizationId) {
            console.error('[MakePayment] No payment authorization found');
            showToast({
                message: 'Complete Payment and Receipt details to continue.',
                type: 'error'
            });
            return;
        }
        
        // Validate receipt information
        if (!receipt?.email || !receipt?.fullName) {
            console.error('[MakePayment] Receipt info incomplete');
            showToast({
                message: 'Please enter your name and email to continue.',
                type: 'error'
            });
            return;
        }
        
        setIsProcessing(true);
        console.log('[MakePayment] 📧 Patching receipt info to session:', {
            sessionUid,
            email: receipt.email,
            fullName: receipt.fullName
        });
        
        try {
            // CRITICAL: Patch receipt info to server session
            // Backend will:
            // 1. Save receipt details to database
            // 2. Trigger "Receipt Details Saved" confirmation email (immediate)
            // 3. Later send final receipt email after charging completes
            const patchResult = await patchReceipt(sessionUid, {
                fullName: receipt.fullName,
                email: receipt.email
            });
            
            if (patchResult.ok) {
                console.log('[MakePayment] ✅ Receipt info patched successfully');
                console.log('[MakePayment] 📧 "Receipt Details Saved" confirmation email triggered');
            } else {
                console.warn('[MakePayment] ⚠️ Receipt patch failed:', patchResult.message);
                // Don't block the flow, but log the error for debugging
                showToast({
                    message: 'Receipt info saved locally. Email may not send.',
                    type: 'warning',
                    duration: 3000
                });
            }

            // Store receipt info locally (for display purposes)
            setReceiptInfo(receipt);
            
            // Store receipt info in SessionContext
            update({
                receiptInfo: {
                    fullName: receipt.fullName,
                    email: receipt.email
                }
            });
            
            // Also store in localStorage for backup
            storeReceiptInfo(sessionUid, {
                fullName: receipt.fullName,
                email: receipt.email,
                chargerId: publicId || stationId,
                timestamp: new Date().toISOString()
            });
            
            console.log('[MakePayment] ✅ Receipt info stored locally for session:', sessionUid);
            
            // Show success toast
            showToast({
                message: 'Receipt saved! Starting your charging session...',
                type: 'success'
            });
            
            // Small delay for UX
            await new Promise(resolve => setTimeout(resolve, 800));
            
            console.log('[MakePayment] Advancing to next step');
            // Move to next step (charging)
            handleIncreaseStep?.();
        } catch (error) {
            console.error('[MakePayment] Error starting charging:', {
                error: error.message,
                stack: error.stack
            });
            showToast({
                message: 'Failed to start charging. Please try again.',
                type: 'error'
            });
        } finally {
            setIsProcessing(false);
        }
    };

    // Handle back button from receipt step. When the credit fully
    // covers the budget there IS no card-details substep to return
    // to, so we bounce all the way back to AmountPicker where the
    // user can remove/edit the credit or change their budget.
    const handleBackToCard = () => {
        if (fullyCovered) {
            console.log('[MakePayment] 🔙 Fully-covered — going back to pre-auth');
            return handleDecreaseStep?.();
        }
        console.log('[MakePayment] 🔙 Going back to card details');
        setPaymentStep(PAYMENT_STEPS.DETAILS);
    };

    // Action buttons configuration - different buttons for each substep
    const actions = currentStep === PAYMENT_STEPS.DETAILS
        ? [
            {
                id: 'authorize',
                label: isAuthorizing ? 'Authorizing…' : isReconciling ? 'Checking…' : 'Authorize & Continue',
                type: 'btn-dark',
                disabled: isAuthorizing || isReconciling
            },
            // onClick: ActionButtonWrapper would otherwise take 'cancel' as
            // its own local-only reset and leave the attempt on the server.
            // Available while authorizing: the server cancel voids any hold,
            // and an approval that lands after it voids itself.
            {
                id: 'cancel',
                label: 'Cancel',
                type: 'btn-outline-dark',
                onClick: confirmAndCancel
            }
        ]
        : currentStep === PAYMENT_STEPS.RECEIPT
        ? [
            {
                id: 'start',
                label: 'Start Charging',
                type: 'btn-dark',
                disabled: isProcessing  // Only disable while processing
            }
        ]
        : [
            // PROCESSING: the driver can always get out of a stuck bank step.
            {
                id: 'cancel',
                label: 'Cancel',
                type: 'btn-outline-dark',
                onClick: confirmAndCancel
            }
        ];

    const handleActionClick = async (id) => {
        if (id === 'cancel') {
            return confirmAndCancel();
        }
        
        if (id === 'authorize') {
            // Trigger authorization from CardAuthorizationStep via ref
            console.log('[MakePayment] 🔘 Authorize button clicked');
            cardStepRef.current?.submit();
        }
        
        if (id === 'start') {
            // Trigger start charging from ReceiptStep via ref
            console.log('[MakePayment] � Start charging button clicked');
            receiptStepRef.current?.submit();
        }
    };

    // PROCESSING substep: full-screen status layout (no scroll, centered)
    if (currentStep === PAYMENT_STEPS.PROCESSING) {
        return (
            <StatusScreen>
                <StatusView
                    status={STATUS.PAYMENT_PROCESSING}
                    message={threeds?.step === 'CHALLENGE' ? 'Complete authentication with your bank...' : 'Processing your payment...'}
                />
                {/* Hidden iframe for 3DS method fingerprinting */}
                {threeds?.methodForm && (
                    <ThreeDSFrame
                        html={threeds.methodForm}
                        visible={false}
                        height={0}
                        width={0}
                    />
                )}
                {/* Visible iframe for 3DS challenge (ACS bank authentication) */}
                {threeds?.showChallenge && threeds?.challengeHtml && (
                    <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
                        <ThreeDSFrame
                            html={threeds.challengeHtml}
                            visible={true}
                            height={400}
                            width={400}
                        />
                    </div>
                )}
                <ActionButtonWrapper ref={ref} actions={actions} onClickAction={handleActionClick} />
            </StatusScreen>
        );
    }

    // DETAILS / RECEIPT substeps: normal scrollable layout
    return (
        <div className='ev-content-wrapper'>
            <EvHeader headerAction={{ 
                label: <BackButton />, 
                action: currentStep === PAYMENT_STEPS.RECEIPT ? handleBackToCard : handleDecreaseStep 
            }} />
            
            <div className="ev-inner-profile-wrapper">
                {currentStep === PAYMENT_STEPS.DETAILS ? (
                    <CardAuthorizationStep
                        ref={cardStepRef}
                        onAuthorize={handleAuthorize}
                        isAuthorizing={isAuthorizing}
                        // Card-side amount only: total budget minus the
                        // applied redeem credit (never negative). The
                        // credit portion is captured on the server via
                        // billing_mode='CREDIT'; the card is asked to
                        // hold the remainder only.
                        amountCents={cardAuthCents}
                        currency={currency}
                        showToast={showToast}
                    />
                ) : (
                    <ReceiptStep
                        ref={receiptStepRef}
                        onStartCharging={handleStartCharging}
                        isProcessing={isProcessing}
                        stationMeta={stationMeta}
                        authData={authData}
                    />
                )}
            </div>

            <ActionButtonWrapper ref={ref} actions={actions} onClickAction={handleActionClick} />
        </div>
    );
};

export default MakePayment;
